<template>
  <bm-control
    v-bind="$attrs"
    :read-only-value="value ? trueLabelOrDefault : falseLabelOrDefault"
  >
    <template #default="{ dataQa }">
      <!-- Pointer hover over the full control (incl. prepend label); keyboard focus on v-switch -->
      <div @mouseenter="onPointerEnter" @mouseleave="onPointerLeave">
        <v-switch
          :input-value="value"
          color="switch"
          v-bind="$attrs"
          :label="trueLabelOrDefault"
          :data-qa="dataQa"
          @change="emit('update:value', $event)"
          @focus="onSwitchFocus"
          @blur="onSwitchBlur"
          @click="onSwitchClick"
        >
          <template #prepend>
            <span
              class="v-label bm-switch-text"
              @click="emit('update:value', !value)"
            >
              {{ falseLabelOrDefault }}
            </span>
          </template>
        </v-switch>
      </div>
      <div
        v-if="errorMessages.length > 0"
        class="v-messages theme--light error--text"
      >
        <div class="v-messages__wrapper">
          <div class="v-messages__message">
            {{ errorMessages[0] }}
          </div>
        </div>
      </div>
    </template>
  </bm-control>
</template>

<script lang="ts">
/**
 * Closely based on v-switch with the main difference being we have a label before the toggle as well as the standard
 * suffixed label.
 *
 * We use the prepend slot for the left label and the existing main label as the appended text.
 */
export default {
  name: "BmSwitch",
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import BmControl from "@/components/controls/input/BmControl.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "@/plugins/i18n";

interface BmSwitchProps {
  value?: boolean;
  trueLabel?: string;
  falseLabel?: string;
  errorMessages?: string[];
}

interface BmSwitchEvents {
  (event: "update:value", value: boolean): void;
  (event: "focus"): void;
  (event: "blur"): void;
  (event: "click", value: MouseEvent): void;
}

const props = withDefaults(defineProps<BmSwitchProps>(), {
  trueLabel: "",
  falseLabel: "",
  errorMessages: () => [],
});

const emit = defineEmits<BmSwitchEvents>();

const { $m } = useI18n();

const keyboardFocused = ref(false);
const pointerInside = ref(false);
const hintActive = computed(() => keyboardFocused.value || pointerInside.value);

watch(hintActive, (active, wasActive) => {
  if (active === wasActive) {
    return;
  }
  if (active) {
    emit("focus");
  } else {
    emit("blur");
  }
});

const onSwitchFocus = () => {
  keyboardFocused.value = true;
};
const onSwitchBlur = () => {
  keyboardFocused.value = false;
};
const onPointerEnter = () => {
  pointerInside.value = true;
};
const onPointerLeave = () => {
  pointerInside.value = false;
};
const onSwitchClick = (event: MouseEvent) => {
  emit("click", event);
};

const trueLabelOrDefault = computed(
  () => props.trueLabel || $m.switch_true_label
);
const falseLabelOrDefault = computed(
  () => props.falseLabel || $m.switch_false_label
);
</script>

<style scoped lang="sass">
@import "src/sass/typography"

// Move margin to padding on the text for proper event handling
.bm-switch-text
  padding-right: 9px

:deep(.v-input__prepend-outer)
  margin-right: 0

.v-input--switch
  cursor: pointer
  user-select: none
  max-width: fit-content

.bm-switch-text, .v-input--switch :deep(.v-label)
  @include bm-body

.v-input--is-disabled :deep(.bm-switch-text)
  color: rgba(#000000, .38)!important

.bm-field label
  margin-bottom: 10px

/** The prepended slot sits too high and need to be pushed down **/
span
  margin-top: 3px

/** We have taken over rendering the error message so hide the one in the original control to not create extra space **/
.v-input--switch :deep(.v-messages)
  display: none

/** Push out to the right and move up a smidge **/
.v-icon
  margin-left: 10px
  position: relative
  top: -3px
</style>
